import { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GqlQuery } from './GqlQuery';

export class GqlQueryWithTransformer<
  Vars extends Record<string, unknown>,
  Data extends Record<string, unknown>,
  Output,
> {
  private _gqlQuery: GqlQuery<Vars, Data>;
  private _transformer: (data: Data) => Output;

  constructor(args: { gqlQuery: GqlQuery<Vars, Data>; transformer: (data: Data) => Output }) {
    this._gqlQuery = args.gqlQuery;
    this._transformer = args.transformer;
  }

  get document() {
    return this._gqlQuery.document;
  }

  get operationName() {
    return this._gqlQuery.operationName;
  }

  async request(
    variables: Vars,
    options?: {
      signal?: AbortSignal;
    },
  ) {
    const result = await this._gqlQuery.request(variables, options);
    return this._transformer(result);
  }

  useQuery(variables: Vars, options?: Omit<UseQueryOptions<Data>, 'queryFn' | 'queryKey'>) {
    const query = this._gqlQuery.useQuery(variables, options);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const data = useMemo(() => {
      if (query.data === undefined) return query.data;
      return this._transformer(query.data);
    }, [query.data]);
    return { ...query, data };
  }

  getQueryKey(variables: Partial<Vars>) {
    return this._gqlQuery.getQueryKey(variables);
  }

  invalidate(variables: Partial<Vars>) {
    this._gqlQuery.invalidate(variables);
  }
}
